import React, { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CalculateGrowthRate = ({ data }) => {
  const pdfRef = useRef();
  const safeData = data ?? [];

  if (!safeData.length) {
    return <p>No data available</p>;
  }

  // =========================
  // Summary Calculations
  // =========================
  const latestYear = safeData[safeData.length - 1].year;

  const totalEmployees = safeData.reduce(
    (sum, row) => sum + (parseInt(row.num_employees) || 0),
    0,
  );

  const highestGrowth = safeData.reduce(
    (max, row) =>
      parseFloat(row.growth_rate || 0) > parseFloat(max.growth_rate || 0)
        ? row
        : max,
    safeData[0],
  );

  // =========================
  // Chart Data
  // =========================
  const chartData = {
    labels: safeData.map((item) => item.year),
    datasets: [
      {
        type: "bar",
        label: "Number of Employees",
        data: safeData.map((item) => parseInt(item.num_employees || 0)),
        backgroundColor: "#014CB2",
        borderRadius: 6,
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Growth Rate (%)",
        data: safeData.map((item) =>
          item.growth_rate !== null ? parseFloat(item.growth_rate) : null,
        ),
        borderColor: "#ef4444",
        backgroundColor: "#ef4444",
        tension: 0.3,
        borderWidth: 2,
        yAxisID: "y2",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employees & Growth Rate (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.label === "Growth Rate (%)") {
              return `${context.parsed.y}%`;
            }
            return context.parsed.y.toLocaleString();
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employees",
        },
        ticks: {
          callback: (value) => value.toLocaleString(),
        },
      },
      y2: {
        position: "right",
        beginAtZero: true,
        title: {
          display: true,
          text: "Growth Rate (%)",
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);

      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("growth_rate_analysis.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Growth Rate Analysis
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Latest Year</p>
            <p className="text-xl font-bold text-blue-800">{latestYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employees (All Years)
            </p>
            <p className="text-xl font-bold text-green-800">
              {totalEmployees.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Growth</p>
            <p className="text-xl font-bold text-yellow-800">
              {highestGrowth.year} ({highestGrowth.growth_rate}%)
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  <th className="p-2">Employees</th>
                  <th className="p-2">Growth (%)</th>
                </tr>
              </thead>
              <tbody>
                {safeData.map((row) => (
                  <tr key={row.year} className="hover:bg-gray-100">
                    <td className="p-2">{row.year}</td>
                    <td className="p-2">
                      {Number(row.num_employees || 0).toLocaleString()}
                    </td>
                    <td className="p-2">
                      {row.growth_rate !== null ? `${row.growth_rate}%` : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-96">
            <Chart type="bar" data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf" />
        </button>
      </div>
    </>
  );
};

export default CalculateGrowthRate;
